/*
 * SCSS → Tailwind token mapping (BuilderOptionsBlock.scss deleted):
 *
 * .bob-options-section:
 *   display:flex / flex-direction:column / gap:10px → flex flex-col gap-[10px]
 *
 * .bob-options-list:
 *   display:flex / flex-direction:column / gap:8px  → flex flex-col gap-[8px]
 *
 * .bob-option-row:
 *   display:flex / align-items:center / gap:10px    → flex items-center gap-[10px]
 *   > :first-child { flex:1 }                       → [&>:first-child]:flex-1
 *
 * .bob-option-error:
 *   font-size:12px / color:--text-error / margin-top:-4px / margin-left:2px
 *   → text-size-2 text-text-error -mt-1 ml-0.5
 *   (-mt-1 ≈ -4px; no exact token; ml-0.5 ≈ 2px)
 *
 * .bob-drag-handle:
 *   display:flex / align-items:center / flex-shrink:0 / cursor:grab / padding:4px 2px
 *   → flex items-center shrink-0 cursor-grab py-1 px-0.5
 *
 * .bob-drag-dots:
 *   width:14px / color:#253845B2                    → w-[14px] text-text-light/70
 *   (text-text-light = --text-medium; /70 for 70% opacity. No exact token for opacity variant.)
 *
 * .bob-add-option:
 *   background:none / border:none / padding:0 / cursor:pointer
 *   font-size:14px / font-weight:600 / color:--highlight / text-align:left / width:fit-content
 *   hover:text-decoration:underline
 *   → bg-transparent border-none p-0 cursor-pointer text-size-3 font-semibold
 *     text-text-highlight text-left w-fit hover:underline
 *   (text-text-highlight = DS token for --highlight / coastalBlue[50] = #178199)
 *
 * Arbitrary values used: gap-[10px], gap-[8px], w-[14px] (no DS token at those sizes).
 */
import { Input, Label } from '@quickmd-llc/design-system'
import { memo } from 'react'
import type { ChangeEvent } from 'react'
import { errorCodes, getUserFriendlyErrorMessage } from '../../../../../utils/errormessages'
import {
  INTAKE_FORM_TYPE_DATE_TEXT,
  INTAKE_FORM_TYPE_EMAIL_TEXT,
  INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_TEXT,
  INTAKE_FORM_TYPE_PHONE_NUMBER_TEXT,
  INTAKE_FORM_TYPE_STATEMENT_TEXT,
  INTAKE_FORM_TYPE_TEXT_BOX_TEXT,
  INTAKE_FORM_TYPE_HEIGHT_WEIGHT_TEXT,
  INTAKE_FORM_TYPE_PREFERRED_PHARMACY_TEXT,
} from '../../../../../utils/constants'

type OptionItem = {
  optionId: string
  optionValue: string
  optionLocalError: string
  isOptionError: boolean
}

type Props = {
  options: OptionItem[]
  builderId: string
  questionTypeValue: string
  onChangeQuestionOptionValue: (builderId: string, optionId: string, value: string) => void
  onChangeQuestionOptionErrorValue: (builderId: string, optionId: string, errorText: string) => void
  onAddOptionIconPress: (initiatorIndex: number, builderId: string) => void
}

const BuilderOptionsBlock = memo(
  ({
    options,
    builderId,
    questionTypeValue,
    onChangeQuestionOptionValue,
    onChangeQuestionOptionErrorValue,
    onAddOptionIconPress,
  }: Props) => {
    const onChangeOptionValue = (optionId: string, e: ChangeEvent<HTMLInputElement>) => {
      onChangeQuestionOptionValue(builderId, optionId, e.target.value)
    }

    const onUserBlur = (optionId: string) => {
      onChangeQuestionOptionErrorValue(builderId, optionId, getUserFriendlyErrorMessage(errorCodes.DUPLICATE_OPTIONS))
    }

    if (
      questionTypeValue === '' ||
      questionTypeValue === INTAKE_FORM_TYPE_STATEMENT_TEXT ||
      questionTypeValue === INTAKE_FORM_TYPE_TEXT_BOX_TEXT ||
      questionTypeValue === INTAKE_FORM_TYPE_EMAIL_TEXT ||
      questionTypeValue === INTAKE_FORM_TYPE_DATE_TEXT ||
      questionTypeValue === INTAKE_FORM_TYPE_FILE_UPLOAD_BOX_TEXT ||
      questionTypeValue === INTAKE_FORM_TYPE_PHONE_NUMBER_TEXT ||
      questionTypeValue === INTAKE_FORM_TYPE_PREFERRED_PHARMACY_TEXT ||
      questionTypeValue === INTAKE_FORM_TYPE_HEIGHT_WEIGHT_TEXT
    ) {
      return null
    }

    return (
      <div className='flex flex-col gap-[10px]'>
        <Label size='md'>Options</Label>

        <div className='flex flex-col gap-[8px]'>
          {options.map((option) => (
            <div
              className='flex items-center gap-[10px] [&>:first-child]:flex-1'
              key={`option-${builderId}-${option.optionId}`}>
              <Input
                value={option.optionValue}
                onChange={(e) => onChangeOptionValue(option.optionId, e)}
                onBlur={() => onUserBlur(option.optionId)}
                placeholder='Type an option'
                error={option.isOptionError}
              />
              {option.isOptionError && option.optionLocalError ?
                <span className='-mt-1 ml-0.5 text-size-2 text-text-error'>{option.optionLocalError}</span>
              : null}
            </div>
          ))}
        </div>

        <button
          className='w-fit cursor-pointer border-none bg-transparent p-0 text-left text-size-3 font-semibold text-text-highlight hover:underline'
          type='button'
          onClick={() => onAddOptionIconPress(options.length - 1, builderId)}>
          + Add option
        </button>
      </div>
    )
  },
)

export { BuilderOptionsBlock }
